
import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { User, Session } from '@supabase/supabase-js';

interface Profile {
  id: string;
  full_name: string | null;
  username: string | null;
  avatar_url: string | null;
  notification_preferences?: any;
  favorite_firms?: string[];
}

interface UserRole {
  id: string;
  user_id: string;
  role: 'trader' | 'firm' | 'admin';
  firm_id: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  userRole: UserRole | null;
  isLoading: boolean;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signInWithGoogle: () => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: any }>;
  hasRole: (role: 'trader' | 'firm' | 'admin') => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    console.log('useAuth: Initializing auth state');
    
    let isMounted = true;

    const initializeAuth = async () => {
      try {
        // Get initial session
        const { data: { session: initialSession } } = await supabase.auth.getSession();
        
        if (!isMounted) return;
        
        console.log('useAuth: Initial session check:', !!initialSession);
        setSession(initialSession);
        setUser(initialSession?.user ?? null);
        
        if (initialSession?.user) {
          console.log('useAuth: User found, fetching profile and role for:', initialSession.user.email);
          await fetchUserData(initialSession.user.id);
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        console.error('useAuth: Error during initialization:', error);
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    // Set up auth state listener
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;
      
      console.log('useAuth: Auth state changed:', event, session?.user?.email);
      
      // Use setTimeout to prevent auth deadlock
      setTimeout(async () => {
        if (!isMounted) return;
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          console.log('useAuth: Fetching user data for:', session.user.email);
          await fetchUserData(session.user.id);
        } else {
          setProfile(null);
          setUserRole(null);
          setIsLoading(false);
        }
      }, 0);
    });

    // Initialize auth
    initializeAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const fetchUserData = async (userId: string) => {
    try {
      console.log('useAuth: Fetching user data for:', userId);
      
      // Fetch both profile and role in parallel
      const [profileResult, roleResult] = await Promise.allSettled([
        fetchUserProfile(userId),
        fetchUserRole(userId)
      ]);

      if (profileResult.status === 'rejected') {
        console.error('useAuth: Error fetching profile:', profileResult.reason);
      }

      if (roleResult.status === 'rejected') {
        console.error('useAuth: Error fetching role:', roleResult.reason);
      }

    } catch (error) {
      console.error('useAuth: Error fetching user data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserProfile = async (userId: string) => {
    try {
      console.log('useAuth: Fetching profile for user:', userId);
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, username, avatar_url, favorite_firms, bookmarked_firms, notification_preferences')
        .eq('id', userId)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('useAuth: Error fetching profile:', error);
        throw error;
      }

      console.log('useAuth: Profile fetched:', data);
      setProfile(data);
      return data;
    } catch (error) {
      console.error('useAuth: Error fetching profile:', error);
      throw error;
    }
  };

  const fetchUserRole = async (userId: string) => {
    try {
      console.log('useAuth: Fetching role for user:', userId);
      // Fetch all roles, prioritize admin > firm > trader (handles users with multiple roles)
      const { data: rolesData, error } = await supabase
        .from('user_roles')
        .select('*')
        .eq('user_id', userId);

      const data = rolesData && rolesData.length > 0
        ? [...rolesData].sort((a, b) => {
            const rank = (r: string) => (r === 'admin' ? 1 : r === 'firm' ? 2 : 3);
            return rank(a.role) - rank(b.role);
          })[0]
        : null;

      if (error || !data) {
        if (!data) {
          console.log('useAuth: No role found for user, creating default trader role');
          // Create default trader role if none exists
          const { data: newRole, error: insertError } = await supabase
            .from('user_roles')
            .insert({
              user_id: userId,
              role: 'trader'
            })
            .select()
            .single();

          if (insertError) {
            console.error('useAuth: Error creating default role:', insertError);
            throw insertError;
          }

          console.log('useAuth: Created default trader role:', newRole);
          setUserRole(newRole);
          return newRole;
        } else {
          console.error('useAuth: Error fetching user role:', error);
          throw error;
        }
      }

      console.log('useAuth: Role fetched:', data);
      setUserRole(data);
      return data;
    } catch (error) {
      console.error('useAuth: Error fetching user role:', error);
      throw error;
    }
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
          emailRedirectTo: 'https://www.verifyprop.com/browsefirms',
        },
      });

      if (error) {
        toast({
          title: 'Sign Up Failed',
          description: error.message,
          variant: 'destructive',
        });
        return { error };
      }

      if (data.user && !data.session) {
        toast({
          title: 'Check Your Email',
          description: 'Please check your email for a confirmation link.',
        });
      } else {
        toast({
          title: 'Welcome!',
          description: 'Your account has been created successfully.',
        });
      }

      return { error: null };
    } catch (error) {
      console.error('Sign up error:', error);
      return { error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        setIsLoading(false);
        toast({
          title: 'Sign In Failed',
          description: error.message,
          variant: 'destructive',
        });
        return { error };
      }

      toast({
        title: 'Welcome Back!',
        description: 'You have been signed in successfully.',
      });

      // Don't set loading to false here - let the auth state change handler do it
      return { error: null };
    } catch (error) {
      setIsLoading(false);
      console.error('Sign in error:', error);
      return { error };
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: 'https://www.verifyprop.com/browsefirms',
        },
      });

      if (error) {
        toast({
          title: 'Google Sign In Failed',
          description: error.message,
          variant: 'destructive',
        });
        return { error };
      }

      return { error: null };
    } catch (error) {
      console.error('Google sign in error:', error);
      return { error };
    }
  };

  const signOut = async () => {
    try {
      console.log('useAuth: Starting sign out process');
      
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('useAuth: Sign out error:', error);
        toast({
          title: 'Sign Out Failed',
          description: error.message,
          variant: 'destructive',
        });
        return;
      }

      console.log('useAuth: Sign out successful');
      
      // Clear local state immediately
      setUser(null);
      setSession(null);
      setProfile(null);
      setUserRole(null);

      toast({
        title: 'Signed Out',
        description: 'You have been signed out successfully.',
      });

    } catch (error) {
      console.error('useAuth: Sign out error:', error);
      toast({
        title: 'Sign Out Failed',
        description: 'An error occurred while signing out.',
        variant: 'destructive',
      });
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: 'https://www.verifyprop.com/auth?mode=reset',
      });

      if (error) {
        toast({
          title: 'Reset Failed',
          description: error.message,
          variant: 'destructive',
        });
        return { error };
      }

      toast({
        title: 'Reset Link Sent',
        description: 'Check your email for the password reset link.',
      });

      return { error: null };
    } catch (error) {
      console.error('Password reset error:', error);
      return { error };
    }
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) return { error: new Error('No user logged in') };

    try {
      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);

      if (error) {
        toast({
          title: 'Update Failed',
          description: error.message,
          variant: 'destructive',
        });
        return { error };
      }

      // Refresh profile data
      await fetchUserProfile(user.id);
      
      toast({
        title: 'Profile Updated',
        description: 'Your profile has been updated successfully.',
      });

      return { error: null };
    } catch (error) {
      console.error('Profile update error:', error);
      return { error };
    }
  };

  const hasRole = (role: 'trader' | 'firm' | 'admin') => {
    return userRole?.role === role;
  };

  const value = {
    user,
    session,
    profile,
    userRole,
    isLoading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
    updateProfile,
    hasRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
